/**
 * アプリのページ origin を返す。
 * `VITE_APPLICATION_DOMAIN` が設定されていればその値、未設定なら現在のページの origin にフォールバック。
 *
 * Preview Deploy 環境のように build 時点でホスト名が決まらない環境では env を空にして runtime fallback を使う。
 * production のように特定 origin に pin したい環境では env を設定する。
 */
export function appOrigin(): string {
  return import.meta.env.VITE_APPLICATION_DOMAIN || window.location.origin
}

/**
 * API リクエスト先の origin を返す。
 * `VITE_API_DOMAIN` が設定されていればその値、未設定なら現在のページの origin にフォールバック。
 *
 * Preview Deploy 環境のように build 時点でホスト名が決まらない環境では env を空にして runtime fallback を使う。
 * production のように特定 origin に pin したい環境では env を設定する。
 */
export function apiOrigin(): string {
  return import.meta.env.VITE_API_DOMAIN || window.location.origin
}

/**
 * URL 文字列のホスト（ポート込み）を返す。空・不正な URL は null
 */
export function hostOf(url: string | undefined): string | null {
  if (!url) return null
  try {
    return new URL(url).host
  } catch {
    return null
  }
}

export function isValidURL(str: string) {
  try {
    new URL(str)
    return true
  } catch (e) {
    return false
  }
}

/**
 * 指定されたリンクがスムーススクロール対象のハッシュリンクかどうかを判定する。
 * ハッシュのみ、または origin と pathname と search が現在ページと一致するリンクを対象にする。
 */
export function isScrollableHashLink(target: Element): target is HTMLAnchorElement {
  if (!(target instanceof HTMLAnchorElement)) return false

  const href = target.getAttribute('href')

  // scrollToHash には target.hash から # を除いた文字列を ID として渡す。
  // 空のハッシュや ? を含むハッシュはスクロール先 ID として扱わず、ブラウザ標準の遷移に任せる。
  // 例: /report/123?hoge=brw_xxx#about1?sort=publish は、target.hash が #about1?sort=publish になるため対象外。
  if (!href || target.hash.length <= 1 || target.hash.includes('?')) return false

  // #about1 のようなハッシュのみのリンクは、現在 URL の query に影響されず同一ページ内リンクとして扱う。
  if (href.startsWith('#')) return true

  // パス付きリンクは現在ページと完全に同じ URL を指す場合のみ、同一ページ内リンクとして扱う。
  // 例: 現在 URL が /report/123?hoge=brw_xxx の場合、/report/123?hoge=brw_xxx#about1 は対象。
  return target.origin === location.origin &&
    target.pathname === location.pathname &&
    target.search === location.search
}

/**
 * 指定のクエリパラメータ取得
 *
 * @param  name {string} パラメータのキー文字列
 * @return  url {url} 対象のURL文字列（任意）
 */
export class UrlUtil {
  private url: URL
  private searchParams: URLSearchParams

  constructor(url?: string) {
    try {
      const baseUrl = window.location.origin
      this.url = url
        ? (isValidURL(url) ? new URL(url) : new URL(url, baseUrl))
        : new URL(window.location.href)

      this.searchParams = new URLSearchParams(this.url.search)
    } catch(e) {
      console.error('Invalid URL string. Fallback to current location.', e)
      this.url = new URL(window.location.href)
      this.searchParams = new URLSearchParams(this.url.search)
    }
  }

  getUrlObject(): URL {
    return this.url
  }

  setQueryParameter(name: string, value: string | number | boolean) {
    this.searchParams.set(name, String(value))
    this.sync()
  }

  deleteQueryParameter(name: string) {
    this.searchParams.delete(name)
    this.sync()
  }

  getQueryParameter(name: string): string | null {
    return this.searchParams.get(name)
  }

  getQueryParameterAll(): Array<[string, string]> {
    return Array.from(this.searchParams.entries())
  }

  getQueryKeys(): string[] {
    return Array.from(this.searchParams.keys())
  }

  getUrlString(): string {
    return this.url.toString()
  }

  getUrlStringWithoutQuery(): string {
    return this.url.origin + this.url.pathname
  }

  getUrlHash(): string {
    return this.url.hash
  }

  hasQuery(name: string): boolean {
    return this.searchParams.has(name)
  }

  /** 内部URLとsearchParamsを同期 */
  private sync(): void {
    this.url.search = this.searchParams.toString()
  }
}
